import React, { useCallback, useEffect, useState } from 'react';
import { LineChart, Loader2, RefreshCcw, PlayCircle } from 'lucide-react';
import api from '@/lib/axios';

export default function MarketplaceAnalyticsPanel() {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [testEvent, setTestEvent] = useState('view_home');
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setDiag((await api.get('/admin/marketplace/analytics/diagnostic')).data); }
    catch (e) { setErr(e.response?.data?.detail || 'Falha a carregar.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runTest = async () => {
    try {
      const res = await api.post('/admin/marketplace/analytics/diagnostic-event', {
        event: testEvent, params: { source: 'painel_diagnostico' }, consent: { analytics: true, ads: false },
      });
      setTestResult(res.data); load();
    } catch (e) { setTestResult({ ok: false, erro: e.response?.data?.detail }); }
  };

  const Row = ({ label, obj }) => (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs">
      <span className="font-semibold text-slate-700">{label}</span>
      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${obj?.estado === 'configurado' || obj?.estado === 'ativo' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
        {obj?.estado}{obj?.origem ? ` · ${obj.origem}` : ''}
      </span>
    </div>
  );

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="analytics-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
          <LineChart className="h-5 w-5 text-emerald-600" /> Google, analítica e Consent Mode (diagnóstico)
        </h2>
        <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600" data-testid="analytics-reload">
          <RefreshCcw className="h-3.5 w-3.5" /> Atualizar
        </button>
      </div>
      {err && <p className="mt-2 text-xs font-semibold text-red-600">{err}</p>}
      {loading ? (
        <p className="mt-4 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> A carregar...</p>
      ) : diag && (
        <>
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            <Row label="GTM" obj={diag.gtm} />
            <Row label="GA4" obj={diag.ga4} />
            <Row label="Google Ads (não ativado)" obj={diag.google_ads} />
            <Row label="Consent Mode v2" obj={diag.consent_mode_v2} />
          </div>

          {(diag.erros_configuracao || []).length > 0 && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800" data-testid="analytics-errors">
              <p className="font-bold">Notas de configuração</p>
              <ul className="mt-1 list-disc pl-4">{diag.erros_configuracao.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}

          <div className="mt-3 rounded-xl border border-slate-200 p-3">
            <p className="text-xs font-bold text-slate-700">Eventos preparados ({(diag.eventos_preparados || []).length})</p>
            <div className="mt-1 flex flex-wrap gap-1">
              {(diag.eventos_preparados || []).map((e) => (
                <span key={e} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{e}</span>
              ))}
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-slate-200 p-3">
            <p className="text-xs font-bold text-slate-700">Testar evento (não é enviado para a Google)</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <select value={testEvent} onChange={(e) => setTestEvent(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" data-testid="analytics-test-select">
                {(diag.eventos_preparados || []).map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
              <button type="button" onClick={runTest} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white" data-testid="analytics-test-run">
                <PlayCircle className="h-3.5 w-3.5" /> Registar evento de teste
              </button>
            </div>
            {testResult && (
              <p className={`mt-2 text-[11px] font-semibold ${testResult.ok ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="analytics-test-result">
                {testResult.ok ? '✅ Evento registado localmente (não enviado).' : `⚠️ ${(testResult.registado?.erros || [testResult.erro]).join('; ')}`}
              </p>
            )}
            {diag.ultimo_evento_diagnostico && (
              <p className="mt-1 text-[10px] text-slate-500">Último: {diag.ultimo_evento_diagnostico.event} · consentimento no teste: analytics={String(diag.consentimento_no_teste?.analytics)} ads={String(diag.consentimento_no_teste?.ads)}</p>
            )}
          </div>
          <p className="mt-2 text-[10px] text-slate-400">Compra só após webhook: {String(diag.compra_apos_webhook)} · valores do backend: {String(diag.valores_monetarios_do_backend)} · comércio ativo: {String(diag.comercio_ativo)}</p>
        </>
      )}
    </section>
  );
}
